import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { ReactComponent as StarSVG } from 'assets/images/star_purple500.svg';
import { ReactComponent as LocationYellow } from 'assets/images/LocationYellow.svg';
import { ReactComponent as LocationBlue } from 'assets/images/LocationBlue.svg';
import Button from 'components/App/Button';
import { LANDMARK_LIST, PLACE_TYPE } from 'constants/index';

import styles from './PlaceDetail.module.scss';

function PlaceDetail({ place, onEdit }) {
  const landmarksLabel = useMemo(() => {
    if (!place?.landmarks?.length) return null;

    const filterLandmarks = place.landmarks.map(
      (item) => LANDMARK_LIST.find((land) => land.value === item)?.label,
    ).filter(Boolean);

    if (!filterLandmarks.length) return null;

    return `Khu vực ${filterLandmarks.join(', ')}`;
  }, [place?.landmarks]);

  const coordinates = place?.location?.coordinates || [];
  const latitude = coordinates[0];
  const longitude = coordinates[1];
  const hasCoordinates = latitude != null && longitude != null;

  const displayTitle = [place?.placeId || place?._id, place?.name]
    .filter(Boolean)
    .join(' - ');

  return (
    <div className={styles.Component}>
      <div className="Information">
        <div className="InformationContent">
          <div className="InformationContentTitle">
            <p className="InformationContentTitle__Name">
              {displayTitle}
            </p>

            {place?.starRating > 0 && (
              <div className="InformationContentTitle__Star">
                {Array.from({ length: place.starRating }, (_, index) => (
                  <StarSVG key={index} />
                ))}
              </div>
            )}
          </div>

          <div className="InformationContentSubTitle">
            {PLACE_TYPE[place?.placeType]?.label && (
              <span className="Text">
                {PLACE_TYPE[place.placeType].label}
              </span>
            )}

            {place?.totalRooms > 0 && (
              <span className="Text">{`Tổng số phòng: ${place.totalRooms}`}</span>
            )}

          </div>

          <div className="InformationContentLocation">
            <LocationYellow />

            <div className="InformationContentLocation_Description">
              <div className="Title">
                {[place?.address, place?.ward, place?.district, place?.province]
                  .filter(Boolean)
                  .join(', ')}
              </div>

              {landmarksLabel && (
                <div className="SubTitle">{landmarksLabel}</div>
              )}
            </div>
          </div>

          {hasCoordinates && (
            <div className="InformationContentLocation">
              <LocationBlue />

              <div className="InformationContentLocation_Description">
                <div className="Title">
                  {`${latitude}, ${longitude}`}
                </div>

                <div className="SubTitle">
                  trên
                  {' '}
                  <a
                    target="_blank"
                    href={`https://www.google.com/maps/place/${latitude},${longitude}`}
                    rel="noreferrer"
                  >
                    <span className="Hyperlink">Google Maps</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          <hr />

          {place?.description && (
            <div className="InformationContentDescription">
              <p className="Title">Miêu tả chỗ nghỉ</p>

              <div
                dangerouslySetInnerHTML={{
                  __html: place.description,
                }}
                className="WhiteSpaceBreakLine"
              />
              <div className="InformationContentDescription__Header">
                <Button
                  classes={styles.EditButton}
                  onClick={onEdit}
                >
                  Chỉnh sửa
                </Button>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PlaceDetail.propTypes = {
  place: PropTypes.shape({
    _id: PropTypes.string,
    placeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    starRating: PropTypes.number,
    placeType: PropTypes.string,
    totalRooms: PropTypes.number,
    isPublished: PropTypes.bool,
    address: PropTypes.string,
    ward: PropTypes.string,
    district: PropTypes.string,
    province: PropTypes.string,
    landmarks: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.shape({
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    description: PropTypes.string,
  }),
  onEdit: PropTypes.func,
};

PlaceDetail.defaultProps = {
  place: null,
  onEdit: () => {},
};

export default memo(PlaceDetail);
